import { AxiosResponse } from 'axios'
import { IPagination } from '../types/pagination'
import { IActivityFilter } from '../views/project/types/activityFilters'
import {
  ICostFilter,
  ICostHistory,
  IDirectCost,
  IMilestone,
  IMilestoneOfferDirectCost,
  IMilestoneOfferDirectCostMonths,
  IMilestoneOfferDirectCostUpdate,
  IPassThroughCost,
} from '../views/project/types/directAndPassTroughCosts'
import {
  ICostBySupplier,
  ICurrentProjectListEntry,
  ICustomer,
  IDepartmentTrend,
  IInsightsDepartmentHourUtilization,
  ILabelMergeTasksBulk,
  ILabelsBulk,
  IOfferDirectCostsBulk,
  IOfferPassThroughCostsBulk,
  IPhasesBulk,
  IProject,
  IProjectBudgetChanges,
  IProjectBudgetChangesBulk,
  IProjectCostSettings,
  IProjectDepartmentsChartData,
  IProjectFilters,
  IProjectLabel,
  IProjectOffer,
  IProjectPhase,
  IProjectRecruitmentStatusChanges,
  IProjectRecruitmentStatusChangesBulk,
  IProjectsHealthWidget,
  IProjectsResponse,
  IProjectsDashboardStats,
  ITimeTrackingProjectsResponse,
  ITrackedHoursByDep
} from '../views/project/types/project'
import {
  ICostByMonths,
  ICostsByPhaseChart,
  IExpendituresByLabels,
  IExpendituresByTasks,
  IInsightsDepartmentExpendituresTimeline,
  IInsightsDepartmentExpendituresTimelineChart,
  IProjectActivity,
} from '../views/project/types/projectActivity'
import { IWizardProject } from '../views/project/types/wizardData'
import { IPaymentsByTimeOverviewChart } from '../types/dashboard'
import { IOfferDirectCost, IOfferPassThroughCost } from '../views/project/types/directAndPassTroughCosts'
import {
  IProjectCostBurndownWidget,
  IProjectTimeTrackingLabel,
  IProjectTimeTrackingTask,
} from '../views/project/types/projectActivity'
import { HttpClient } from './service'
import Urls from './urls'

export const ProjectsApi = {
  getProjects(queryParams: IProjectFilters): Promise<IProjectsResponse> {
    return HttpClient.get<IProjectsResponse, IProjectFilters>(Urls.Projects.projects, queryParams)
  },

  getTimeTrackingProjects(): Promise<ITimeTrackingProjectsResponse> {
    return HttpClient.get<ITimeTrackingProjectsResponse, IProjectFilters>(Urls.Projects.timeTrackingProjects)
  },

  createProject(project: IProject | IWizardProject): Promise<IProject> {
    return HttpClient.post<IProject, IProject | IWizardProject>(Urls.Projects.projects, project)
  },

  getProject(id: string | number, queryParams?: Record<string, string>): Promise<IProject> {
    return HttpClient.get<IProject, Record<string, string>>(`${Urls.Projects.projects}${id}/`, queryParams)
  },

  updateProject(project: IProject): Promise<IProject> {
    return HttpClient.put<IProject, IProject>(Urls.Projects.projects, project.id, project)
  },

  getProjectsHealthWidget(): Promise<IProjectsHealthWidget> {
    return HttpClient.get<IProjectsHealthWidget, IProjectFilters>(`${Urls.Projects.projects}health-widget`)
  },

  getDashboardStats(): Promise<IProjectsDashboardStats> {
    return HttpClient.get<IProjectsDashboardStats, null>(`${Urls.Projects.projects}dashboard-stats/`)
  },

  getProjectsInDanger(): Promise<IProject[]> {
    return HttpClient.get<IProject[], null>(`${Urls.Projects.projects}in-danger/`)
  },

  getActivities(prId: string | number, queryParams?: Record<string, string>): Promise<IProjectActivity[]> {
    return HttpClient.get<IProjectActivity[], Record<string, string>>(
      `${Urls.Projects.projects}${prId}/activities/`,
      queryParams
    )
  },

  getPaginatedActivities(prId: string | number, queryParams: Record<string, string>): Promise<IPagination<IProjectActivity[]>> {
    return HttpClient.get<IPagination<IProjectActivity[]>, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/activities/`,
      queryParams
    )
  },

  getActFilters(prId: string | number): Promise<IActivityFilter[]> {
    return HttpClient.get<IActivityFilter[], null>(`${Urls.Projects.projects}${prId}/activity-filters/`)
  },

  deleteProject(project: IProject): Promise<IProject> {
    return HttpClient.delete<IProject, IProject>(Urls.Projects.projects, project.id)
  },

  updateProjectPhases(projectId: string | number, phase: IPhasesBulk): Promise<IProjectPhase[]> {
    return HttpClient.put<IProjectPhase[], IPhasesBulk>(
      `${Urls.Projects.projects}${projectId}/phases/update-list/`,
      undefined,
      phase
    )
  },

  updateProjectLabels(projectId: string | number, label: ILabelsBulk): Promise<IProjectLabel[]> {
    return HttpClient.put<IProjectLabel[], ILabelsBulk>(
      `${Urls.Projects.projects}${projectId}/labels/update-list/`,
      undefined,
      label
    )
  },

  mergeProjectTaskWithLabels(
    projectId: string | number,
    taskBulkLabel: ILabelMergeTasksBulk
  ): Promise<IProjectActivity[]> {
    return HttpClient.put<IProjectActivity[], ILabelMergeTasksBulk>(
      `${Urls.Projects.projects}${projectId}/activities/update-list/`,
      undefined,
      taskBulkLabel
    )
  },

  mergeProjectTaskWithLabelsDirectCost(
    projectId: string | number,
    taskBulkLabel: ILabelMergeTasksBulk
  ): Promise<IDirectCost[]> {
    return HttpClient.put<IDirectCost[], ILabelMergeTasksBulk>(
      `${Urls.Projects.projects}${projectId}/activities/update-list/`,
      undefined,
      taskBulkLabel
    )
  },

  updateProjectBillingCost(projectId: string | number, billingCostSettings: IProjectCostSettings): Promise<IProject> {
    return HttpClient.put<IProject, IProjectCostSettings>(
      `${Urls.Projects.projects}${projectId}/project-cost-settings/`,
      undefined,
      billingCostSettings
    )
  },

  getDepartmentCostChartData(
    projectId: string | number,
    queryParams?: Record<string, string>
  ): Promise<IProjectDepartmentsChartData> {
    return HttpClient.get<IProjectDepartmentsChartData, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/department-costs/`,
      queryParams
    )
  },

  getDepartmentExpendituresTimeline(
    projectId: number | string,
    queryParams?: Record<string, string>
  ): Promise<IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline> {
    return HttpClient.get<
      IInsightsDepartmentExpendituresTimelineChart | IInsightsDepartmentExpendituresTimeline,
      Record<string, string>
    >(`${Urls.Projects.projects}${projectId}/department-expenditures/`, queryParams)
  },

  getProjectExpendituresBurndown(projectId: number | string): Promise<IProjectCostBurndownWidget> {
    return HttpClient.get<IProjectCostBurndownWidget, IProjectCostBurndownWidget>(
      `${Urls.Projects.projects}${projectId}/expenditures-burndown/`
    )
  },

  getProjectExpendituresByLabels(projectId: number | string): Promise<IExpendituresByLabels[]> {
    return HttpClient.get<IExpendituresByLabels[], IExpendituresByLabels[]>(
      `${Urls.Projects.projects}${projectId}/expenditures-by-labels/`
    )
  },

  getProjectExpendituresByTasks(
    projectId: number | string,
    queryParams?: Record<string, string>
  ): Promise<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>> {
    return HttpClient.get<IExpendituresByTasks[] | IPagination<IExpendituresByTasks[]>, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/expenditures-by-tasks/`,
      queryParams
    )
  },

  getCostByMonths(projectId: number | string): Promise<ICostByMonths[]> {
    return HttpClient.get<ICostByMonths[], ICostByMonths[]>(`${Urls.Projects.projects}${projectId}/cost-per-months/`)
  },

  getProjectTimeTrackingLabels(
    projectId: number | string,
    queryParams: Record<string, string>
  ): Promise<IProjectTimeTrackingLabel[] | IPagination<IProjectTimeTrackingLabel[]>> {
    return HttpClient.get<IProjectTimeTrackingLabel[], Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/time-tracking-labels/`,
      queryParams
    )
  },

  getProjectTimeTrackingTasks(
    projectId: number | string,
    queryParams?: Record<string, string>
  ): Promise<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>> {
    return HttpClient.get<IProjectTimeTrackingTask[] | IPagination<IProjectTimeTrackingTask[]>, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/time-tracking-tasks/`,
      queryParams
    )
  },

  getCostByPhases(projectId: number | string): Promise<ICostsByPhaseChart> {
    return HttpClient.get<ICostsByPhaseChart, ICostsByPhaseChart>(
      `${Urls.Projects.projects}${projectId}/cost-per-phases/`
    )
  },

  getDepHoursUtil(
    projectId: number | string,
    queryParams?: Record<string, string>
  ): Promise<IInsightsDepartmentHourUtilization> {
    return HttpClient.get<IInsightsDepartmentHourUtilization, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/department-hours-util/`,
      queryParams
    )
  },

  getDepartmentTrend(projectId: number | string, queryParams?: Record<string, string>): Promise<IDepartmentTrend> {
    return HttpClient.get<IDepartmentTrend, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/department-trend-hours/`,
      queryParams
    )
  },

  getTrackedHoursByDepartment(
    projectId: string | number,
    queryParams?: Record<string, string>
  ): Promise<ITrackedHoursByDep[]> {
    return HttpClient.get<ITrackedHoursByDep[], Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/tracked-hours-by-dep/`,
      queryParams
    )
  },

  updateProjectBudgetChanges(
    projectId: string | number,
    budget: IProjectBudgetChangesBulk
  ): Promise<IProjectBudgetChanges[]> {
    return HttpClient.put<IProjectBudgetChanges[], IProjectBudgetChangesBulk>(
      `${Urls.Projects.projects}${projectId}/budget-change/update-list/`,
      undefined,
      budget
    )
  },

  getProjectBudgetChanges(
    projectId: string | number,
    queryParams?: Record<string, string>
  ): Promise<IProjectBudgetChanges[]> {
    return HttpClient.get<IProjectBudgetChanges[], Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/budget-change/`,
      queryParams
    )
  },

  getDirectCosts(prId: string | number, queryParams?: Record<string, string>): Promise<IDirectCost[]> {
    return HttpClient.get<IDirectCost[], Record<string, string>>(
      `${Urls.Projects.projects}${prId}/direct-costs/`,
      queryParams
    )
  },

  getPaginatedDirectCosts(prId: string | number, queryParams: Record<string, string>): Promise<IPagination<IDirectCost[]>> {
    return HttpClient.get<IPagination<IDirectCost[]>, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/direct-costs/`,
      queryParams
    )
  },

  getPassTroughCosts(prId: string | number, queryParams?: Record<string, string>): Promise<IPassThroughCost[]> {
    return HttpClient.get<IPassThroughCost[], Record<string, string>>(
      `${Urls.Projects.projects}${prId}/pass-trough-costs/`,
      queryParams
    )
  },

  getOfferPassTroughCosts(
    prId: string | number,
    offer_id: string | number,
    queryParams?: Record<string, string>
  ): Promise<IOfferPassThroughCost[]> {
    return HttpClient.get<IOfferPassThroughCost[], Record<string, string>>(
      `${Urls.Projects.projects}${prId}/offers/${offer_id}/offer-pass-trough-cost`,
      queryParams
    )
  },

  getOfferDirectCosts(
    prId: string | number,
    offer_id: string | number,
    queryParams?: Record<string, string>
  ): Promise<IOfferDirectCost[]> {
    return HttpClient.get<IOfferDirectCost[], Record<string, string>>(
      `${Urls.Projects.projects}${prId}/offers/${offer_id}/offer-direct-cost/`,
      queryParams
    )
  },

  getDirectCost(projectId: number | string, cost: IDirectCost): Promise<IDirectCost> {
    return HttpClient.get<IDirectCost, IDirectCost>(`${Urls.Projects.projects}${projectId}/direct-costs/${cost.id}/`)
  },

  getPassTroughCost(projectId: number | string, cost: IPassThroughCost): Promise<IPassThroughCost> {
    return HttpClient.get<IPassThroughCost, IPassThroughCost>(
      `${Urls.Projects.projects}${projectId}/pass-trough-costs/${cost.id}/`
    )
  },

  getDirectOrPassTroughCostsFilters(prId: string | number): Promise<ICostFilter[]> {
    return HttpClient.get<ICostFilter[], null>(`${Urls.Projects.projects}${prId}/cost-filters/`)
  },

  createDirectCost(projectId: number | string, cost: IDirectCost): Promise<IDirectCost> {
    return HttpClient.post<IDirectCost, IDirectCost>(`${Urls.Projects.projects}${projectId}/direct-costs/`, cost)
  },

  updateDirectCost(projectId: number | string, cost: IDirectCost): Promise<IDirectCost> {
    return HttpClient.put<IDirectCost, IDirectCost>(
      `${Urls.Projects.projects}${projectId}/direct-costs/`,
      cost.id,
      cost
    )
  },

  updateOfferDirectCosts(
    projectId: string | number,
    offer_id: string | number,
    costs: IOfferDirectCostsBulk
  ): Promise<IOfferDirectCost[]> {
    return HttpClient.put<IOfferDirectCost[], IOfferDirectCostsBulk>(
      `${Urls.Projects.projects}${projectId}/offers/${offer_id}/offer-direct-cost/update-list/`,
      undefined,
      costs
    )
  },

  createPassTroughCost(projectId: number | string, cost: IPassThroughCost): Promise<IPassThroughCost> {
    return HttpClient.post<IPassThroughCost, IPassThroughCost>(
      `${Urls.Projects.projects}${projectId}/pass-trough-costs/`,
      cost
    )
  },

  updatePassTroughCost(projectId: number | string, cost: IPassThroughCost): Promise<IPassThroughCost> {
    return HttpClient.put<IPassThroughCost, IPassThroughCost>(
      `${Urls.Projects.projects}${projectId}/pass-trough-costs/`,
      cost.id,
      cost
    )
  },

  updateOfferPassTroughCosts(
    projectId: string | number,
    offer_id: string | number,
    costs: IOfferPassThroughCostsBulk
  ): Promise<IOfferPassThroughCost[]> {
    return HttpClient.put<IOfferPassThroughCost[], IOfferPassThroughCostsBulk>(
      `${Urls.Projects.projects}${projectId}/offers/${offer_id}/offer-pass-trough-cost/update-list/`,
      undefined,
      costs
    )
  },

  deleteProjectOffer(projectId: number | string, offer: IProjectOffer): Promise<string | number> {
    return HttpClient.delete<string | number, string | number>(
      `${Urls.Projects.projects}${projectId}/offers/`,
      offer.id
    )
  },

  getDirectCostHistory(projectId: number | string, cost: IDirectCost, search: string): Promise<ICostHistory[]> {
    return HttpClient.get<ICostHistory[], { search: string }>(
      `${Urls.Projects.projects}${projectId}/direct-costs/${cost.id}/history`,
      { search: search }
    )
  },

  getPassTroughCostHistory(
    projectId: number | string,
    cost: IPassThroughCost,
    search: string
  ): Promise<ICostHistory[]> {
    return HttpClient.get<ICostHistory[], { search: string }>(
      `${Urls.Projects.projects}${projectId}/pass-trough-costs/${cost.id}/history`,
      { search: search }
    )
  },

  getCostsBySupplier(projectId: number | string, queryParams?: Record<string, string>): Promise<ICostBySupplier[]> {
    return HttpClient.get<ICostBySupplier[], Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/costs-by-supplier/`,
      queryParams
    )
  },

  getCustomers(): Promise<ICustomer[]> {
    return HttpClient.get<ICustomer[], null>(Urls.Projects.customers)
  },

  getPaymentsByTimeChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/payments-by-time`,
      queryParams
    )
  },

  getPaymentForecastChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/payment-forecast`,
      queryParams
    )
  },

  getReceiptForecastChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/receipt-forecast`,
      queryParams
    )
  },

  getExpenditurePhasingChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/expenditure-phasing`,
      queryParams
    )
  },

  getCostDevelopmentChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/cost-development`,
      queryParams
    )
  },

  updateProjectRecruitmentStatusChanges(
    projectId: string | number,
    status: IProjectRecruitmentStatusChangesBulk
  ): Promise<IProjectRecruitmentStatusChanges[]> {
    return HttpClient.put<IProjectRecruitmentStatusChanges[], IProjectRecruitmentStatusChangesBulk>(
      `${Urls.Projects.projects}${projectId}/recruitment-status-change/update-list/`,
      undefined,
      status
    )
  },

  getProjectRecruitmentStatusChanges(
    projectId: string | number,
    queryParams?: Record<string, string>
  ): Promise<IProjectRecruitmentStatusChanges[]> {
    return HttpClient.get<IProjectRecruitmentStatusChanges[], Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/recruitment-status-change/`,
      queryParams
    )
  },

  getProjectRecruitmentStatusChartData(
    projectId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${projectId}/recruitment-costs`,
      queryParams
    )
  },

  getProjectMarginDevelopmentChartData(
    prId: number,
    queryParams?: Record<string, string>
  ): Promise<IPaymentsByTimeOverviewChart> {
    return HttpClient.get<IPaymentsByTimeOverviewChart, Record<string, string>>(
      `${Urls.Projects.projects}${prId}/margin-development`,
      queryParams
    )
  },

  getCurrentProjectsList(queryParams: IProjectFilters): Promise<ICurrentProjectListEntry[]> {
    return HttpClient.get<ICurrentProjectListEntry[], IProjectFilters>(Urls.Projects.currentProjects, queryParams)
  },

  getRestrictedPhases(projectId: number): Promise<number[]> {
    return HttpClient.get<number[], IProjectFilters>(`${Urls.Projects.projects}${projectId}/restricted-phases`)
  },

  exportProjectActivities(projectId: number, queryParams?: Record<string, string>): Promise<Blob | AxiosResponse<Blob>> {
    return HttpClient.downloadFile<Record<string, string>>(`${Urls.Projects.projects}${projectId}/activities/export`, queryParams)
  },

  exportProjectDirectCosts(projectId: number, queryParams?: Record<string, string>): Promise<Blob | AxiosResponse<Blob>> {
    return HttpClient.downloadFile<Record<string, string>>(`${Urls.Projects.projects}${projectId}/direct-costs/export`, queryParams)
  },

  exportProjectOfferDirectCosts(queryParams?: Record<string, string | boolean | number>): Promise<Blob | AxiosResponse<Blob>> {
    return HttpClient.downloadFile<Record<string, string | boolean | number>>(`${Urls.Projects.projects}offer-direct-costs/export`, queryParams)
  },

  exportProjectOfferPassThroughCosts(queryParams?: Record<string, string | boolean | number>): Promise<Blob | AxiosResponse<Blob>> {
    return HttpClient.downloadFile<Record<string, string | boolean | number>>(`${Urls.Projects.projects}offer-pass-through-costs/export`, queryParams)
  },

  getMilestoneOfferDirectCosts(id: number, queryParams?: Record<string, string>): Promise<IMilestoneOfferDirectCost[]> {
    return HttpClient.get<IMilestoneOfferDirectCost[], Record<string, string>>(
      `${Urls.Projects.projects}${id}/milestone-offer-direct-costs`,
      queryParams
    )
  },

  getProjectMilestones(id: number, queryParams?: Record<string, string>): Promise<IMilestone[]> {
    return HttpClient.get<IMilestone[], Record<string, string>>(
      `${Urls.Projects.projects}${id}/milestones`,
      queryParams
    )
  },

  getMilestone(id: number): Promise<IMilestone> {
    return HttpClient.get<IMilestone, number>(`${Urls.Projects.milestones}${id}`)
  },

  deleteMilestone(id: number): Promise<number> {
    return HttpClient.delete<number, number>(Urls.Projects.milestones, id)
  },

  createMilestone(id: number, milestone: IMilestone): Promise<IMilestone> {
    return HttpClient.post<IMilestone, IMilestone>(
      `${Urls.Projects.projects}${id}/milestones/`,
      milestone
    )
  },

  updateMilestone(milestone: IMilestone): Promise<IMilestone> {
    return HttpClient.put<IMilestone, IMilestone>(
      Urls.Projects.milestones,
      milestone.id,
      milestone
    )
  },

  updateMilestoneOfferDirectCosts(id: number, tasks: IMilestoneOfferDirectCostUpdate[]): Promise<IMilestoneOfferDirectCost[]> {
    return HttpClient.post<IMilestoneOfferDirectCost[], IMilestoneOfferDirectCostUpdate[]>(
      `${Urls.Projects.projects}${id}/unit-tracker/`,
      tasks
    )
  },

  getProjectMilestonesUnitTracker(id: number, queryParams?: Record<string, string>): Promise<IMilestoneOfferDirectCostMonths[]> {
    return HttpClient.get<IMilestoneOfferDirectCostMonths[], Record<string, string>>(
      `${Urls.Projects.projects}${id}/unit-tracker`,
      queryParams
    )
  },
}
